import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@pella/sash-framework';
import { useAPI } from '@pella/cosmos-react';
import { useUser } from '@pella/react-layout';
import { QUERY_KEYS } from '../../utils/enums';

/**
 * @typedef {object} AddTeamMemberAppPrefHandlerParams
 * @property {object} preference
 * @property {string} prefId
 */

/**
 * Custom Hook to handle user Api mutations
 */
export const useUserMutation = () => {
  const { api } = useAPI();
  const { userid, appPreferences } = useUser();
  const queryClient = useQueryClient();

  const preparePreference = ({ preference, prefId }) => {
    let newPreference = structuredClone(appPreferences);
    // check if the user has any preferences or the right structure
    // if not, create a new preference object
    if (!newPreference?.preferences) {
      newPreference = { preferences: [{ prefId, preference }] };
      return newPreference;
    }
    // check if the user has the preference already and if we are adding or updating an existing preference
    const foundElement = newPreference.preferences.find((item) => item.prefId === prefId);
    if (foundElement) {
      foundElement.preference = preference;
    } else {
      newPreference.preferences.push({ prefId, preference });
    }
    return newPreference;
  };

  /**
   * Function to handle the Team Member Preferences mutation
   * @param {AddTeamMemberAppPrefHandlerParams} preference
   */
  const addTeamMemberAppPrefHandler = async ({ preference, prefId }) => {
    const updatedPreference = preparePreference({ preference, prefId });
    await api.applUser.addTeamMemberAppPreferences(userid, updatedPreference);
    return updatedPreference;
  };

  /**
   * Mutation to add new Team Member Preference Note
   * @see https://react-query.tanstack.com/guides/mutations
   */
  const addTeamMemberAppPref = useMutation({
    mutationFn: addTeamMemberAppPrefHandler,
    onSuccess: (preferences) => {
      queryClient.setQueryData([QUERY_KEYS.TEAM_MEMBER_APP_PREFERENCES, userid], preferences);
    },
    onError: (error) => {
      toast.error(`Table settings save error: ${error.message}`);
    },
  });

  return {
    addTeamMemberAppPref,
  };
};
